import { type Domain } from "@/composables/domain-manager";
import { type Process } from "@/composables/process-manager";
import { type Product } from "@/composables/product-manager";
import { Artefact } from "@/lib/artefacts";
import { followLink } from "@/lib/openapi/helpers";
import { Event } from "@/stores/event-store";
import { Flow } from "@/stores/flow-store";
import { Gateway } from "@/stores/gateway-store";
import { type Stek } from "@/stores/steks-store";
import { type Task } from "@/stores/task-store";

type HasDescription = {
    identity: string;
    description?: {
        identity: string;
        mimetype: string;
        links?: Record<string, unknown>;
    };
};

export async function fetchMarkdownArtefact(
    apiEntity: HasDescription,
    artefactRef:
        | {
              identity: string;
              mimetype: string;
              links?: Record<string, unknown>;
          }
        | undefined,
    name: string,
    fallbackIdentitySuffix: string,
    signal?: AbortSignal,
    query?: URLSearchParams
): Promise<Artefact<"text/markdown">> {
    if (artefactRef) {
        let mimetype = artefactRef.mimetype;
        // Upgrade plain text to markdown.
        if (mimetype === "text/plain") {
            mimetype = "text/markdown";
        }
        if (mimetype !== "text/markdown") {
            throw Error(
                `Can only handle markdown descriptions! Not ${mimetype}`
            );
        }

        const { response, data } = await followLink<File>(
            artefactRef,
            "get",
            undefined,
            signal,
            query
        );

        if (!response.ok) {
            return Artefact.createEmptyArtefact({
                mimetype: "text/markdown",
                identity: artefactRef.identity,
            });
        }

        // Assuming data might already be the blob or response data
        const blob = data instanceof Blob ? data : new Blob([data || ""]);

        return new Artefact({
            name,
            identity: artefactRef.identity,
            blob: blob,
            mimetype: mimetype,
            content: await blob.text(),
        });
    } else {
        return Artefact.createEmptyArtefact({
            mimetype: "text/markdown",
            identity: apiEntity.identity + fallbackIdentitySuffix,
        });
    }
}

export async function getDescriptionArtefact(
    apiEntity: Domain | Product | Process | Task | Gateway | Event | Flow,
    name: string,
    signal?: AbortSignal,
    query?: URLSearchParams
): Promise<Artefact<"text/markdown">> {
    return fetchMarkdownArtefact(
        apiEntity,
        apiEntity.description,
        name,
        "_description",
        signal,
        query
    );
}

export async function getMissionArtefact(
    apiEntity: Stek,
    name: string,
    signal?: AbortSignal,
    query?: URLSearchParams
): Promise<Artefact<"text/markdown">> {
    return fetchMarkdownArtefact(
        apiEntity,
        apiEntity.mission,
        name,
        "_mission",
        signal,
        query
    );
}

export async function getVisionArtefact(
    apiEntity: Stek,
    name: string,
    signal?: AbortSignal,
    query?: URLSearchParams
): Promise<Artefact<"text/markdown">> {
    return fetchMarkdownArtefact(
        apiEntity,
        apiEntity.vision,
        name,
        "_vision",
        signal,
        query
    );
}

export async function getMissionVisionArtefact(
    apiEntity: Stek,
    name: string,
    signal?: AbortSignal,
    query?: URLSearchParams
): Promise<Artefact<"text/markdown">> {
    return fetchMarkdownArtefact(
        apiEntity,
        apiEntity.mission_vision,
        name,
        "_mission_vision",
        signal,
        query
    );
}

export async function getCoreValuesArtefact(
    apiEntity: Stek,
    name: string,
    signal?: AbortSignal,
    query?: URLSearchParams
): Promise<Artefact<"text/markdown">> {
    return fetchMarkdownArtefact(
        apiEntity,
        apiEntity.core_values,
        name,
        "_core_values",
        signal,
        query
    );
}

export async function getSpecializationsArtefact(
    apiEntity: Stek,
    name: string,
    signal?: AbortSignal,
    query?: URLSearchParams
): Promise<Artefact<"text/markdown">> {
    return fetchMarkdownArtefact(
        apiEntity,
        apiEntity.specializations,
        name,
        "_specializations",
        signal,
        query
    );
}

export async function getTargetMarketsArtefact(
    apiEntity: Stek,
    name: string,
    signal?: AbortSignal,
    query?: URLSearchParams
): Promise<Artefact<"text/markdown">> {
    return fetchMarkdownArtefact(
        apiEntity,
        apiEntity.target_markets,
        name,
        "_target_markets",
        signal,
        query
    );
}
